import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Button,
    Stack,
    Badge,
    Tooltip,
    Box,
} from '@mui/material';
import {
    Menu as MenuIcon,
    History as HistoryIcon,
    Add as AddIcon,
    Logout as LogoutIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Shield as ShieldIcon,
    ConfirmationNumber as ManageCategoryRequestIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toggle, open, close } from '@/store/uiSlice';
import { useColorScheme } from '@mui/material/styles';
import { useI18nContext } from '@/i18n/i18n-react';
import {
    useLogoutMutation,
    useGetSettingsQuery,
    useGetUserInfoQuery,
    useGetAdminCategoryRequestsQuery,
    useGetResponsibleCategoryRequestsQuery,
    useUpdateUserInfoMutation,
} from '@/store/apiSlice';
import ResponsiveButton from '@/components/ResponsiveButton';
import { useNavigate } from 'react-router-dom';
import { useRef, useEffect } from 'react';

interface AppBarProps {
    variant?: 'main' | 'admin';
    isAdmin?: boolean;
}

const CustomAppBar = ({ variant = 'main' }: AppBarProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { history } = useAppSelector((state) => state.ui.open);
    const { mode, setMode } = useColorScheme();
    const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
    const [updateUserInfo] = useUpdateUserInfoMutation();
    const { data: settings } = useGetSettingsQuery();
    const { data: userInfo } = useGetUserInfoQuery();
    const { LL } = useI18nContext();

    const isResponsible = (userInfo?.categoriesResponsible?.length ?? 0) > 0;
    const canReviewRequests = userInfo?.admin || isResponsible;

    const adminPending = useGetAdminCategoryRequestsQuery(
        { status: 'pending' },
        { skip: variant === 'admin' || !canReviewRequests || !userInfo?.admin }
    );
    const responsiblePending = useGetResponsibleCategoryRequestsQuery(
        { status: 'pending' },
        { skip: variant === 'admin' || !canReviewRequests || userInfo?.admin || !isResponsible }
    );

    const pendingCount = userInfo?.admin
        ? (adminPending.data?.data?.length ?? 0)
        : (responsiblePending.data?.data?.length ?? 0);

    const handleIconClick = () => {
        variant === 'admin' ? dispatch(close('admin')) : dispatch(toggle('menu'));
    };

    const handleLogout = async () => {
        try {
            const result = await logout().unwrap();
            if (result.oidc && result.logoutUrl) {
                window.location.href = result.logoutUrl;
            } else {
                window.location.href = '/';
            }
        } catch (error) {
            console.error('Logout failed:', error);
            window.location.href = '/';
        }
    };

    const scrollRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
        }
    }, []);

    return (
        <AppBar
            position="sticky"
            elevation={1}
            sx={{
                bgcolor: 'background.default',
                borderRadius: 0,
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Toolbar
                variant="dense"
                sx={{
                    minHeight: { xs: 48, sm: 56 },
                    gap: variant === 'admin' ? 0 : 1,
                    overflow: 'hidden',
                }}
            >
                {/* Menu / back icon — never shrinks */}
                <IconButton
                    edge="start"
                    color="primary"
                    onClick={handleIconClick}
                    sx={{ mr: 1, flexShrink: 0 }}
                >
                    <MenuIcon />
                </IconButton>

                {/* Title — never shrinks */}
                <Typography
                    variant="h6"
                    color={variant === 'admin' ? undefined : 'primary'}
                    sx={{
                        flexShrink: 0,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        fontWeight: 700,
                    }}
                >
                    {variant === 'admin' ? LL.appBar.adminPanel() : LL.appBar.plan()}
                </Typography>

                {/* Flexible spacer */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }} />

                {variant === 'admin' ? (
                    <Stack direction="row" sx={{ gap: 1, flexShrink: 0 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => dispatch(close('admin'))}
                        >
                            {LL.common.back()}
                        </Button>
                        <IconButton
                            color="default"
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                            title={LL.appBar.logout()}
                        >
                            <LogoutIcon />
                        </IconButton>
                    </Stack>
                ) : (
                    /* Scrollable action strip — no scrollbar visible, touch/trackpad swipe works */
                    <Box
                        ref={scrollRef}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            // Full bar height: overflowX also clips vertically, which cut off the badge
                            alignSelf: 'stretch',
                            gap: 0.5,
                            minWidth: 0,
                            overflowX: 'auto',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                        }}
                    >
                        <ResponsiveButton
                            icon={<PersonIcon />}
                            label={LL.menu.profile()}
                            onClick={() => dispatch(open('user'))}
                            color="secondary"
                        />
                        {settings?.mail?.enabled && (
                            <ResponsiveButton
                                icon={<EmailIcon />}
                                label={LL.menu.mailbox()}
                                onClick={() => dispatch(open('mailbox'))}
                                color="primary"
                            />
                        )}
                        <ResponsiveButton
                            icon={<HistoryIcon />}
                            label={LL.appBar.history()}
                            onClick={() => dispatch(toggle('history'))}
                            color={history ? 'secondary' : 'inherit'}
                            variant={history ? 'contained' : 'outlined'}
                        />
                        {canReviewRequests && (
                            <Badge
                                badgeContent={pendingCount}
                                color="warning"
                                overlap="rectangular"
                                anchorOrigin={{ vertical: 'bottom' }}
                                invisible={pendingCount === 0}
                                // Lifted so it fits the 48px mobile bar below a 36px button
                                sx={{ '& .MuiBadge-badge': { bottom: 4 } }}
                            >
                                <ResponsiveButton
                                    icon={<ManageCategoryRequestIcon />}
                                    label={LL.menu.reviewCategoryRequests()}
                                    onClick={() => dispatch(open('categoryRequestsReview'))}
                                    color="error"
                                />
                            </Badge>
                        )}
                        {(userInfo?.admin || userInfo?.eventEditor) && (
                            <ResponsiveButton
                                icon={<AddIcon />}
                                label={LL.appBar.event()}
                                onClick={() => dispatch(open('event'))}
                                color="error"
                            />
                        )}
                        {userInfo?.admin && (
                            <IconButton
                                color="error"
                                onClick={() => navigate('/admin')}
                                title={LL.menu.adminPanel()}
                            >
                                <ShieldIcon />
                            </IconButton>
                        )}
                        <Tooltip
                            title={
                                (mode ?? 'dark') === 'dark'
                                    ? LL.appBar.switchToLight()
                                    : LL.appBar.switchToDark()
                            }
                        >
                            <IconButton
                                color="default"
                                onClick={() => {
                                    const next = (mode ?? 'dark') === 'dark' ? 'light' : 'dark';
                                    setMode(next);
                                    updateUserInfo({ theme: next });
                                }}
                            >
                                {(mode ?? 'dark') === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            color="default"
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                            title={LL.appBar.logout()}
                        >
                            <LogoutIcon />
                        </IconButton>
                    </Box>
                )}
            </Toolbar>
        </AppBar>
    );
};

export default CustomAppBar;
